import { ArrowUpRightIcon, ChevronRightIcon } from 'lucide-react'
import { motion } from 'motion/react'
import React from 'react'
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from '#app/components/ui/avatar.tsx'
import { Badge } from '#app/components/ui/badge.tsx'
import { Card, CardContent, CardHeader } from '#app/components/ui/card.tsx'
import { cn } from '#app/utils/misc.tsx'

interface ResumeCardProps {
	logoUrl: string
	altText: string
	title: string
	subtitle?: string
	href?: string
	badges?: readonly string[]
	period: string
	description?: string
}
export const ResumeCard = ({
	logoUrl,
	altText,
	title,
	subtitle,
	href,
	badges,
	period,
	description,
}: ResumeCardProps) => {
	// Default open: the descriptions are the substance of the section, so make
	// them visible without a click; the chevron becomes a collapse affordance.
	const [isExpanded, setIsExpanded] = React.useState(true)
	const expandable = Boolean(description)

	// Discrete, valid interactive controls (no nested <a>/<button>):
	//   - <a> icon-link to visit `href`
	//   - <button> to toggle the description
	// Both are siblings inside a non-interactive container.
	return (
		<div className="block">
			<Card className="flex border-none shadow-none">
				<div className="mt-1 flex-none p-4">
					<Avatar className="bg-muted-background dark:bg-foreground m-auto size-12 border">
						<AvatarImage
							src={logoUrl}
							alt={altText}
							className="object-contain"
						/>
						<AvatarFallback>{altText[0]}</AvatarFallback>
					</Avatar>
				</div>
				<div className="group flex-grow flex-col items-center">
					<CardHeader className="p-4">
						<div className="flex items-center justify-between gap-x-2 text-base">
							<h3 className="inline-flex items-center gap-x-1 text-xs leading-none font-semibold sm:text-sm">
								{title}
								{badges && (
									<span className="inline-flex gap-x-1">
										{badges.map((badge, index) => (
											<Badge
												variant="secondary"
												className="align-middle text-xs"
												key={index}
											>
												{badge}
											</Badge>
										))}
									</span>
								)}
								{href && (
									<a
										href={href}
										target="_blank"
										rel="noreferrer"
										aria-label={`Visit ${title}`}
										className="text-muted-foreground hover:text-foreground focus-visible:text-foreground rounded-sm transition-colors"
									>
										<ArrowUpRightIcon className="size-3.5" />
									</a>
								)}
							</h3>
							<div className="flex items-center gap-x-2">
								<span className="text-muted-foreground text-right text-xs tabular-nums sm:text-sm">
									{period}
								</span>
								{expandable && (
									<button
										type="button"
										onClick={() => setIsExpanded((v) => !v)}
										aria-expanded={isExpanded}
										aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${title}`}
										className="text-muted-foreground hover:text-foreground focus-visible:text-foreground rounded-sm"
									>
										<ChevronRightIcon
											className={cn(
												'size-4 transition-transform duration-300 ease-out',
												isExpanded ? 'rotate-90' : 'rotate-0',
											)}
										/>
									</button>
								)}
							</div>
						</div>
						{subtitle && <div className="font-sans text-xs">{subtitle}</div>}
					</CardHeader>
					{description && (
						<CardContent className="p-0">
							<motion.div
								initial={false}
								animate={{
									opacity: isExpanded ? 1 : 0,
									height: isExpanded ? 'auto' : 0,
								}}
								transition={{
									duration: 0.7,
									ease: [0.16, 1, 0.3, 1],
								}}
								className="overflow-hidden"
							>
								{/* `whitespace-pre-line` so a description can break itself into
								    labelled paragraphs (Ingestion: / Analytics: / …) with \n\n.
								    Entries without newlines are unaffected. */}
								<div className="px-4 pb-4 text-xs whitespace-pre-line sm:text-sm">
									{description}
								</div>
							</motion.div>
						</CardContent>
					)}
				</div>
			</Card>
		</div>
	)
}
